import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, ReceiptText, DollarSign, Package, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { parseQuantidadeNaoNegativa } from '../lib/validators';
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const FORM_INITIAL = {
  numero_nf: '', data_emissao: '', pedido_id: '', valor_total: '',
  numero_parcelas: '1', data_p1: '', data_p2: '', data_p3: '',
  qtde_entregue: '', motivo_entrega: '',
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const inpSm = "w-full border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const getVariacaoInfo = (qtdePedido, qtdeEntregue) => {
  if (!qtdePedido || !qtdeEntregue) return null;
  const variacao = ((qtdeEntregue - qtdePedido) / qtdePedido) * 100;
  const abs = Math.abs(variacao);
  const sinal = variacao >= 0 ? '+' : '';
  if (abs <= 10) return { cor: 'bg-emerald-100 text-emerald-800 border-emerald-300', semaforo: '🟢', label: `${sinal}${variacao.toFixed(1)}%`, ok: true };
  if (abs <= 20) return { cor: 'bg-orange-100 text-orange-800 border-orange-300', semaforo: '🟡', label: `${sinal}${variacao.toFixed(1)}%`, ok: false };
  return { cor: 'bg-red-100 text-red-800 border-red-300', semaforo: '🔴', label: `${sinal}${variacao.toFixed(1)}%`, ok: false };
};

// Converte string BR ("4.938,77") pra número. Retorna null em QUALQUER falha
// (vazio, texto inválido, NaN) — nunca 0 por fallback silencioso, pra não
// confundir "usuário digitou zero" com "não deu pra converter".
const parseBRLNumber = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// qtde_entregue vem de <input type="number"> nativo — não tem separador de
// milhar pra remover (browser não deixa digitar vírgula, e ponto aqui só
// pode ser decimal). Quantidade é sempre inteira: rejeita decimal em vez de
// truncar silenciosamente (parseInt("12,5") teria virado 12 sem avisar).
const parseQuantity = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  return Number.isInteger(n) ? n : null;
};

// Espelha backend/config.py::LIMITE_DIVERGENCIA_NF_PCT — mudar os dois juntos.
const LIMITE_DIVERGENCIA_NF_PCT = 3.0;

// Espelha backend/utils/divergencia.py::CAUSAS_DIVERGENCIA — mesmos values, labels pro usuário.
const CAUSAS_DIVERGENCIA = [
  { value: 'sobre_entrega', label: 'Sobre-entrega' },
  { value: 'reajuste_preco', label: 'Reajuste de preço' },
  { value: 'ipi', label: 'IPI' },
  { value: 'desconto', label: 'Desconto' },
  { value: 'erro_correcao', label: 'Correção de erro anterior' },
  { value: 'outro', label: 'Outro' },
];

// Espelha backend/utils/divergencia.py::calcular_divergencia. qtde_entregue
// vazio (null) é tratado como 0 entregue — nunca pula a checagem — pra bater
// exatamente com o que o backend recalcula no POST /notas-fiscais.
const calcularDivergencia = (pedido, valores) => {
  const round2 = (n) => Math.round(n * 100) / 100;

  const valorPedido = Number(pedido?.valor_total) || 0;
  const valorNf = valores.valorTotal || 0;
  const valorDiff = valorPedido ? Math.abs(valorNf - valorPedido) / valorPedido * 100 : 0;

  const qtdePedido = Number(pedido?.quantidade) || 0;
  const qtdeNf = valores.qtdeEntregue || 0; // null vira 0 de propósito — espelha o backend
  const qtdeDiff = qtdePedido ? Math.abs(qtdeNf - qtdePedido) / qtdePedido * 100 : 0;

  return {
    valor_diff_pct: round2(valorDiff),
    qtde_diff_pct: round2(qtdeDiff),
    divergencia_pct: round2(Math.max(valorDiff, qtdeDiff)),
  };
};

// valida os campos vulneráveis ao bug "parseFloat/parseInt || 0 mascarando
// entrada inválida como zero" (caso PINCEIS ROMA). qtde_entregue vazio é
// estado legítimo (sem tracking de variação de entrega) — só bloqueia
// quando preenchido com algo que não é um inteiro > 0.
const validateNF = (formData) => {
  const errors = {};
  const valorTotal = parseBRLNumber(formData.valor_total);
  if (valorTotal === null || valorTotal <= 0) {
    errors.valor_total = 'Informe um valor de NF maior que zero';
  }
  let qtdeEntregue = null;
  if (String(formData.qtde_entregue ?? '').trim() !== '') {
    qtdeEntregue = parseQuantity(formData.qtde_entregue);
    if (qtdeEntregue === null || qtdeEntregue <= 0) {
      errors.qtde_entregue = 'Quantidade entregue deve ser maior que zero (ou deixe em branco)';
    }
  }
  return { valid: Object.keys(errors).length === 0, errors, valores: { valorTotal, qtdeEntregue } };
};

const NovaNF = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();
  const pedidoIdFromUrl = searchParams.get('pedido_id');

  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(FORM_INITIAL);
  const [erros, setErros] = useState({});
  const [pdfExtraido, setPdfExtraido] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [itemSelectionData, setItemSelectionData] = useState(null);
  const [selectedItemIdx, setSelectedItemIdx] = useState(0);
  const [itensEntrega, setItensEntrega] = useState([]);
  const [divergenciaPendente, setDivergenciaPendente] = useState(null);

  const pedidoSelecionado = pedidos.find(p => p.id === formData.pedido_id);
  const qtdePedida = pedidoSelecionado?.quantidade || 0;
  const previewVariacao = formData.qtde_entregue && qtdePedida
    ? getVariacaoInfo(qtdePedida, parseInt(formData.qtde_entregue))
    : null;

  const inpCls = (field) => pdfExtraido[field]
    ? 'w-full border-2 border-emerald-400 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400/10 transition-all'
    : inp;

  const inpSmCls = (field) => pdfExtraido[field]
    ? 'w-full border-2 border-emerald-400 rounded-lg bg-emerald-50 px-3 h-9 text-xs font-semibold text-slate-800 outline-none focus:border-emerald-500 transition-all cursor-pointer'
    : `${inpSm} cursor-pointer`;

  const limparErro = (campo) => setErros(prev => (prev[campo] ? { ...prev, [campo]: undefined } : prev));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pedidosRes = await api.get('/pedidos');
        const pedidosList = pedidosRes.data || [];
        setPedidos(pedidosList);
        if (pedidoIdFromUrl) {
          const found = pedidosList.find(p => p.id === pedidoIdFromUrl);
          if (found) {
            setFormData(prev => ({
              ...prev,
              pedido_id: pedidoIdFromUrl,
              valor_total: String(found.valor_total || '').replace('.', ','),
              qtde_entregue: String(found.quantidade || ''),
            }));
          }
        }
      } catch {
        toast.error('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Inicializa itensEntrega quando o pedido selecionado muda (multi-item)
  useEffect(() => {
    if (!pedidoSelecionado) { setItensEntrega([]); return; }
    const itens = pedidoSelecionado.itens || [];
    if (itens.length <= 1) { setItensEntrega([]); return; }
    const entrega = itens.map(item => ({
      ...item,
      qtde_entregue: item.quantidade || 0,
      valor_item: item.subtotal || 0,
    }));
    setItensEntrega(entrega);
    const totalQtde = entrega.reduce((s, it) => s + (it.quantidade || 0), 0);
    const totalValor = entrega.reduce((s, it) => s + (it.subtotal || 0), 0);
    setFormData(prev => ({
      ...prev,
      qtde_entregue: String(totalQtde),
      valor_total: new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalValor),
    }));
  }, [formData.pedido_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsePdf = async (file) => {
    setPdfLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
      let fullText = '';
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const content = await page.getTextContent();
        let lastY = null;
        for (const item of content.items) {
          if (!item.str) continue;
          const y = item.transform?.[5];
          if (lastY !== null && y !== undefined && Math.abs(y - lastY) > 5) fullText += '\n';
          fullText += item.str;
          if (y !== undefined) lastY = y;
        }
        fullText += '\n';
      }

      const updates = {};
      const extraidos = {};

      const mNF = fullText.match(/OS PRODUTOS CONSTANTES DA NOTA FISCAL INDICADA AO LADO\n0*(\d+)/);
      if (mNF) { updates.numero_nf = mNF[1]; extraidos.numero_nf = true; }

      const mData = fullText.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\s+(\d{2}\/\d{2}\/\d{4})$/m);
      if (mData) {
        const [d, m, a] = mData[1].split('/');
        updates.data_emissao = `${a}-${m}-${d}`;
        extraidos.data_emissao = true;
      }

      const mParcelas = [...fullText.matchAll(/(\d{7})\/(\d{3})\s+(\d{2}\/\d{2}\/\d{4})\s+([\d.,]+)/g)];
      if (mParcelas.length > 0) {
        updates.numero_parcelas = String(Math.min(mParcelas.length, 3));
        extraidos.numero_parcelas = true;
        const camposDatas = ['data_p1', 'data_p2', 'data_p3'];
        mParcelas.slice(0, 3).forEach((match, i) => {
          const [dd, mm, aa] = match[3].split('/');
          updates[camposDatas[i]] = `${aa}-${mm}-${dd}`;
          extraidos[camposDatas[i]] = true;
        });
      }

      const toN = (s) => parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
      // Número BR ancorado (ex: "4.938,77"): auto-delimitado pela vírgula + 2 casas decimais,
      // pois o pdf.js às vezes extrai colunas numéricas coladas sem espaço entre si.
      const NUM_BR = String.raw`\d+(?:\.\d{3})*,\d{2}`;
      // Coluna de quantidade pode vir com 2 ou 3 casas decimais (ex: "10,275"), diferente
      // das colunas monetárias que sempre têm exatamente 2 (NUM_BR). Quantificador NÃO-guloso
      // (`{2,3}?`): quando colunas vêm coladas sem espaço, prefere 2 casas e só expande para 3
      // se isso for necessário para o resto do padrão casar — evita roubar dígito do campo seguinte.
      const QTD_BR = String.raw`\d+(?:\.\d{3})*,\d{2,3}?`;
      const mItens = [...fullText.matchAll(new RegExp(
        `\\d{9}\\s+(.+?)\\s+48\\d{6}\\s+\\d{3}\\s+\\d{4}\\s+MI\\s*(${QTD_BR})\\s*(${NUM_BR})\\s*(${NUM_BR})\\s*${NUM_BR}\\s*${NUM_BR}\\s*(${NUM_BR})`,
        'g'
      ))];

      if (mItens.length === 1) {
        const qtdeMilheiros = toN(mItens[0][2]);
        const valorTotal = toN(mItens[0][4]);
        if (valorTotal > 0) {
          updates.valor_total = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valorTotal);
          extraidos.valor_total = true;
        }
        if (qtdeMilheiros > 0) {
          updates.qtde_entregue = String(Math.round(qtdeMilheiros * 1000));
          extraidos.qtde_entregue = true;
        }
        const count = Object.keys(extraidos).length;
        setFormData(prev => ({ ...prev, ...updates }));
        setPdfExtraido(extraidos);
        if (count > 0) toast.success(`✓ ${count} campos preenchidos automaticamente`);
        else toast.error('Nenhum campo reconhecido no PDF');
      } else if (mItens.length > 1) {
        const items = mItens.map(match => ({
          descricao: match[1].trim(),
          qtdeMilheiros: toN(match[2]),
          valorTotal: toN(match[4]),
        }));
        setFormData(prev => ({ ...prev, ...updates }));
        setPdfExtraido(extraidos);
        setItemSelectionData({ items, numero_nf_raw: updates.numero_nf || '' });
        setSelectedItemIdx(0);
      } else {
        // Layout ALFA EMBALAGENS — cada item em 1 linha:
        // {CODIGO} {DESCRICAO} {NCM-8d} {CST-3d} {CFOP-4d} {UNID} {QTDE} {VL_UNIT} 0,00 {VL_LIQUIDO} ...
        // m[1]=código  m[2]=descrição  m[3]=qtde  m[4]=vl_liquido (4º nº após unidade)
        const mItensAlfa = [...fullText.matchAll(
          /^(\S+)\s+(.+?)\s+\d{8}\s+\d{3}\s+\d{4}\s+[A-Z]{1,3}\s+([\d.,]+)\s+[\d.,]+\s+[\d.,]+\s+([\d.,]+)/gm
        )];

        if (mItensAlfa.length > 0) {
          // numero_nf: "Nº 000.008.479" → strip pontos → "000008479"
          if (!updates.numero_nf) {
            const mNFAlfa = fullText.match(/n[°º]\s+(\d{3}\.\d{3}\.\d{3})/i)
              || fullText.match(/NF-?e\s+n[°oOoº]\.?\s*([\d.]+)/i)
              || fullText.match(/N[Úú]MERO\s*:?\s*([\d.]+)/i);
            if (mNFAlfa) { updates.numero_nf = mNFAlfa[1].replace(/\./g, ''); extraidos.numero_nf = true; }
          }

          // data_emissao: "DATA DA EMISSÃO 25/05/2026"
          if (!updates.data_emissao) {
            const mDataAlfa = fullText.match(/DATA\s+DA\s+EMISS[ÃA][OÕ]\s+(\d{2}\/\d{2}\/\d{4})/i);
            if (mDataAlfa) {
              const [dd, mm, aa] = mDataAlfa[1].split('/');
              updates.data_emissao = `${aa}-${mm}-${dd}`;
              extraidos.data_emissao = true;
            }
          }

          // parcelas/vencimentos: seção após "Número : Vencimento : Valor :"
          if (!updates.numero_parcelas) {
            const partesParcelas = fullText.split(/N[úu]mero\s*:\s*Vencimento\s*:\s*Valor\s*:/i);
            if (partesParcelas.length > 1) {
              const mDatasVenc = [...partesParcelas[1].matchAll(/(\d{2}\/\d{2}\/\d{4})/g)].slice(0, 3);
              if (mDatasVenc.length > 0) {
                updates.numero_parcelas = String(mDatasVenc.length);
                extraidos.numero_parcelas = true;
                const camposDatas = ['data_p1', 'data_p2', 'data_p3'];
                mDatasVenc.forEach((match, i) => {
                  const [dd, mm, aa] = match[1].split('/');
                  updates[camposDatas[i]] = `${aa}-${mm}-${dd}`;
                  extraidos[camposDatas[i]] = true;
                });
              }
            }
          }

          // valor_total = soma dos vl_liquido de cada item; qtde = soma das quantidades
          let somaValor = 0;
          let somaQtde = 0;
          for (const m of mItensAlfa) {
            somaQtde += toN(m[3]);
            somaValor += toN(m[4]);
          }
          if (somaValor > 0) {
            updates.valor_total = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(somaValor);
            extraidos.valor_total = true;
          }
          if (somaQtde > 0) {
            updates.qtde_entregue = String(Math.round(somaQtde));
            extraidos.qtde_entregue = true;
          }

          const count = Object.keys(extraidos).length;
          setFormData(prev => ({ ...prev, ...updates }));
          setPdfExtraido(extraidos);
          if (count > 0) toast.success(`✓ ${count} campos preenchidos (Alfa Embalagens)`);
          else toast.error('Nenhum campo reconhecido no PDF');
        } else {
          const count = Object.keys(extraidos).length;
          setFormData(prev => ({ ...prev, ...updates }));
          setPdfExtraido(extraidos);
          if (count > 0) toast.success(`✓ ${count} campos preenchidos automaticamente`);
          else toast.error('Nenhum campo reconhecido no PDF');
        }
      }
    } catch {
      toast.error('Erro ao ler o PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleConfirmarItem = () => {
    if (!itemSelectionData) return;
    const { items, numero_nf_raw } = itemSelectionData;
    const item = items[selectedItemIdx];
    const prefixo = '0'.repeat(selectedItemIdx);
    setFormData(prev => ({
      ...prev,
      numero_nf: prefixo + numero_nf_raw,
      valor_total: new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.valorTotal),
      qtde_entregue: String(Math.round(item.qtdeMilheiros * 1000)),
    }));
    setPdfExtraido(prev => ({ ...prev, numero_nf: true, valor_total: true, qtde_entregue: true }));
    setItemSelectionData(null);
    toast.success(`✓ Item ${selectedItemIdx + 1} selecionado — campos preenchidos automaticamente`);
  };

  const handleQtdeEntregaItem = (idx, valor) => {
    limparErro('qtde_entregue');
    const { ok, qtde, erro } = parseQuantidadeNaoNegativa(valor);
    if (!ok) {
      toast.error(erro);
      return;
    }
    const novo = itensEntrega.map((it, i) => {
      if (i !== idx) return it;
      const ratio = it.quantidade > 0 ? qtde / it.quantidade : 0;
      return { ...it, qtde_entregue: qtde, valor_item: (it.subtotal || 0) * ratio };
    });
    setItensEntrega(novo);
    const totalQtde = novo.reduce((s, it) => s + (it.qtde_entregue || 0), 0);
    const totalValor = novo.reduce((s, it) => s + (it.valor_item || 0), 0);
    setFormData(prev => ({
      ...prev,
      qtde_entregue: String(totalQtde),
      valor_total: new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalValor),
    }));
  };

  const submeterNF = async (causa, valores) => {
    setSubmitting(true);
    try {
      const payload = {
        numero_nf: formData.numero_nf,
        data_emissao: formData.data_emissao,
        pedido_id: formData.pedido_id,
        valor_total: valores.valorTotal,
        numero_parcelas: parseInt(formData.numero_parcelas),
        datas_manuais: [formData.data_p1, formData.data_p2, formData.data_p3].filter(Boolean),
        qtde_entregue: valores.qtdeEntregue,
        motivo_entrega: formData.motivo_entrega || null,
        ...(causa ? { divergencia_pedido: { causa } } : {}),
      };
      await api.post('/notas-fiscais', payload);

      if (valores.qtdeEntregue && pedidoSelecionado) {
        const qtdeEntregue = valores.qtdeEntregue;
        const qtdePed = pedidoSelecionado.quantidade || 0;
        const variacao = qtdePed > 0 ? ((qtdeEntregue - qtdePed) / qtdePed * 100).toFixed(1) : 0;

        let itensEntregues;
        if (itensEntrega.length > 0) {
          // multi-item: usa qtde por item definida na tabela
          itensEntregues = itensEntrega.map(item => {
            const qEnt = item.qtde_entregue || 0;
            const ratio = item.quantidade > 0 ? qEnt / item.quantidade : 0;
            const novoSubtotal = (item.subtotal || 0) * ratio;
            const novoPeso = (item.peso_calculado || 0) * ratio;
            const novaComissaoItem = novoSubtotal * (item.comissao_percent || 0) / 100;
            return { ...item, quantidade: qEnt, peso_calculado: novoPeso, subtotal: novoSubtotal, comissao_valor: novaComissaoItem };
          });
        } else {
          // single-item: aplica ratio global sobre o pedido
          const ratio = qtdePed > 0 ? qtdeEntregue / qtdePed : 1;
          itensEntregues = (pedidoSelecionado.itens || []).map(item => {
            const novoSubtotal = (item.subtotal || 0) * ratio;
            const novoPeso = (item.peso_calculado || 0) * ratio;
            const novaQtdeItem = Math.round((item.quantidade || 0) * ratio);
            const novaComissaoItem = novoSubtotal * (item.comissao_percent || 0) / 100;
            return { ...item, quantidade: novaQtdeItem, peso_calculado: novoPeso, subtotal: novoSubtotal, comissao_valor: novaComissaoItem };
          });
        }

        const novoPesoTotal = itensEntregues.reduce((s, it) => s + (it.peso_calculado || 0), 0);
        const novoValorTotal = itensEntregues.reduce((s, it) => s + (it.subtotal || 0), 0);
        const novaComissao = itensEntregues.reduce((s, it) => s + (it.comissao_valor || 0), 0);

        await api.put(`/pedidos/${pedidoSelecionado.id}`, {
          ...pedidoSelecionado,
          quantidade: qtdeEntregue,
          peso_total: novoPesoTotal,
          valor_total: novoValorTotal > 0 ? novoValorTotal : pedidoSelecionado.valor_total,
          comissao_valor: novaComissao > 0 ? novaComissao : pedidoSelecionado.comissao_valor,
          status: 'NF_EMITIDA',
          historico_entrega: {
            qtde_pedida: qtdePed, qtde_entregue: qtdeEntregue,
            variacao_percent: variacao,
            motivo: formData.motivo_entrega || 'cliente_aceitou',
            registrado_em: new Date().toISOString(),
          },
          itens: itensEntregues,
        });

        if (formData.motivo_entrega === 'entrega_parcial') {
          const saldo = qtdePed - qtdeEntregue;
          if (saldo > 0) {
            const numMae = pedidoSelecionado.numero_fabrica || '';
            let itensSaldo;
            if (itensEntrega.length > 0) {
              // multi-item: saldo por item = quantidade original - qtde_entregue
              itensSaldo = itensEntrega.map(item => {
                const qSaldo = Math.max(0, (item.quantidade || 0) - (item.qtde_entregue || 0));
                const ratio = item.quantidade > 0 ? qSaldo / item.quantidade : 0;
                const subtotalSaldo = (item.subtotal || 0) * ratio;
                const pesoSaldoItem = (item.peso_calculado || 0) * ratio;
                const comissaoSaldoItem = subtotalSaldo * (item.comissao_percent || 0) / 100;
                return { ...item, quantidade: qSaldo, subtotal: subtotalSaldo, peso_calculado: pesoSaldoItem, comissao_valor: comissaoSaldoItem };
              });
            } else {
              const ratioSaldo = qtdePed > 0 ? saldo / qtdePed : 0;
              itensSaldo = (pedidoSelecionado.itens || []).map(item => {
                const qtdeSaldoItem = Math.round((item.quantidade || 0) * ratioSaldo);
                const subtotalSaldo = (item.subtotal || 0) * ratioSaldo;
                const pesoSaldoItem = (item.peso_calculado || 0) * ratioSaldo;
                const comissaoSaldoItem = subtotalSaldo * (item.comissao_percent || 0) / 100;
                return { ...item, quantidade: qtdeSaldoItem, subtotal: subtotalSaldo, peso_calculado: pesoSaldoItem, comissao_valor: comissaoSaldoItem };
              });
            }
            const pesoSaldo = itensSaldo.reduce((s, it) => s + (it.peso_calculado || 0), 0);
            const valorSaldo = itensSaldo.reduce((s, it) => s + (it.subtotal || 0), 0);
            const comissaoSaldo = itensSaldo.reduce((s, it) => s + (it.comissao_valor || 0), 0);
            await api.post('/pedidos', {
              cliente_id: pedidoSelecionado.cliente_id,
              cliente_nome: pedidoSelecionado.cliente_nome,
              item_nome: pedidoSelecionado.item_nome,
              numero_fabrica: `${numMae}-S1`,
              numero_fe: pedidoSelecionado.numero_fe,
              numero_oc: pedidoSelecionado.numero_oc,
              condicao_pagamento: pedidoSelecionado.condicao_pagamento,
              data_entrega: pedidoSelecionado.data_entrega,
              quantidade: saldo, peso_total: pesoSaldo,
              valor_total: valorSaldo, comissao_valor: comissaoSaldo,
              status: 'IMPLANTADO', pedido_mae: pedidoSelecionado.numero_fabrica,
              observacoes: `Saldo do pedido ${pedidoSelecionado.numero_fabrica}`,
              itens: itensSaldo,
            });
            toast.success('Pedido saldo criado!');
          }
        }
      }

      toast.success('NF lançada e vencimentos gerados!');
      navigate('/notas-fiscais');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar nota fiscal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAdmin) return toast.error('Acesso negado');
    if (submitting) return;

    const { valid, errors, valores } = validateNF(formData);
    if (!valid) {
      setErros(errors);
      toast.error('Corrija os campos destacados antes de salvar');
      return;
    }
    setErros({});

    const div = calcularDivergencia(pedidoSelecionado, valores);
    if (div.divergencia_pct > LIMITE_DIVERGENCIA_NF_PCT) {
      setDivergenciaPendente({ ...div, valores });
      return; // abre modal, NÃO submete
    }
    submeterNF(null, valores);
  };

  const confirmarCausa = (causa) => {
    const v = divergenciaPendente.valores;
    setDivergenciaPendente(null);
    submeterNF(causa, v);
  };

  const pedidosDisponiveis = pedidos.filter(p => p.status !== 'NF_EMITIDA' && p.status !== 'CANCELADO');

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        className="hidden"
        onChange={(e) => { if (e.target.files[0]) parsePdf(e.target.files[0]); e.target.value = ''; }}
      />

      {/* MODAL — Seleção de item quando NF tem múltiplos itens */}
      <Dialog open={!!itemSelectionData} onOpenChange={(open) => { if (!open) setItemSelectionData(null); }}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-5 bg-[#0A3D73] text-white">
            <DialogTitle className="text-xs uppercase tracking-widest font-black">
              Esta NF tem {itemSelectionData?.items?.length} itens — Qual deseja faturar agora?
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-2">
            {itemSelectionData?.items?.map((item, idx) => (
              <label
                key={idx}
                className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer text-xs font-semibold transition-all ${
                  selectedItemIdx === idx
                    ? 'bg-blue-50 border-[#0A3D73] text-slate-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="item_nf"
                  checked={selectedItemIdx === idx}
                  onChange={() => setSelectedItemIdx(idx)}
                  className="mt-0.5 accent-[#0A3D73]"
                />
                <div>
                  <span className="font-black text-slate-800">Item {idx + 1}:</span>{' '}
                  {item.descricao}
                  <div className="font-mono text-[#0A3D73] font-black mt-0.5">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorTotal)}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 px-6 pb-5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setItemSelectionData(null)}
              className="px-5 py-2.5 text-xs font-black uppercase border border-slate-200 bg-white text-slate-600 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmarItem}
              className="px-5 py-2.5 text-xs font-black uppercase bg-[#0A3D73] hover:bg-[#082D54] text-white rounded-xl transition-colors"
            >
              Confirmar seleção
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MODAL — Divergência de valor/quantidade em relação ao pedido (backend exige causa acima de 3%) */}
      <Dialog open={!!divergenciaPendente} onOpenChange={(open) => { if (!open) setDivergenciaPendente(null); }}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-5 bg-[#0A3D73] text-white">
            <DialogTitle className="text-xs uppercase tracking-widest font-black">
              Divergência em relação ao pedido
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-3">
            <p className="text-xs font-semibold text-slate-600">
              A NF difere do pedido em{' '}
              <span className="font-black text-red-600">{divergenciaPendente?.divergencia_pct}%</span>.
              Selecione o motivo para continuar:
            </p>
            <div className="flex gap-4 text-[11px] font-bold text-slate-500">
              <span>Valor: {divergenciaPendente?.valor_diff_pct}%</span>
              <span>Qtde: {divergenciaPendente?.qtde_diff_pct}%</span>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              {CAUSAS_DIVERGENCIA.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => confirmarCausa(c.value)}
                  className="px-3 py-2.5 text-xs font-black uppercase border border-slate-200 bg-white text-slate-600 rounded-xl hover:border-[#0A3D73] hover:text-[#0A3D73] transition-colors"
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end px-6 pb-5 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setDivergenciaPendente(null)}
              className="px-5 py-2.5 text-xs font-black uppercase text-slate-400 hover:text-red-600 transition-colors"
            >
              ✕ Cancelar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <Link to="/notas-fiscais" className="p-2 rounded-lg bg-white border border-slate-200 hover:border-[#0A3D73] hover:text-[#0A3D73] text-slate-500 transition-all">
            <ArrowLeft size={16}/>
          </Link>
          <div>
            <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
              <ReceiptText size={22} className="text-[#0A3D73]"/> Registrar Faturamento (NF)
            </h1>
            <p className="text-xs text-slate-500 font-semibold mt-0.5">Emissão de nota fiscal e geração de vencimentos</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={pdfLoading}
          className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          <FileText size={14}/>
          {pdfLoading ? '⏳ Lendo PDF...' : '📎 Importar PDF da NF'}
        </button>
      </div>

      {/* FORM — 2 colunas */}
      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* COLUNA ESQUERDA */}
          <div className="space-y-4">

            {/* 01 · Identificação */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-1.5 mb-4">01 · Identificação</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Número da NF * {pdfExtraido.numero_nf && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                  <div className="relative">
                    <Input value={formData.numero_nf} onChange={e => setFormData({...formData, numero_nf:e.target.value})} required className={inpCls('numero_nf')} placeholder="000.000"/>
                    {pdfExtraido.numero_nf && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                  </div>
                </div>
                <div>
                  <label className={lbl}>Data de Emissão * {pdfExtraido.data_emissao && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                  <div className="relative">
                    <Input type="date" value={formData.data_emissao} onChange={e => setFormData({...formData, data_emissao:e.target.value})} required className={inpCls('data_emissao')}/>
                    {pdfExtraido.data_emissao && <span className="absolute right-7 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                  </div>
                </div>
                <div>
                  <label className={lbl}>Nº de Parcelas {pdfExtraido.numero_parcelas && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                  <select value={formData.numero_parcelas} onChange={e => setFormData({...formData, numero_parcelas:e.target.value})} className={inpSmCls('numero_parcelas')}>
                    <option value="1">1 parcela</option>
                    <option value="2">2 parcelas</option>
                    <option value="3">3 parcelas</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={lbl}>Pedido Origem *</label>
                  <select value={formData.pedido_id} onChange={e => setFormData({...formData, pedido_id:e.target.value})} className={`${inpSm} cursor-pointer`} required>
                    <option value="">— Selecione o pedido —</option>
                    {pedidosDisponiveis.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.numero_fabrica||'S/REF'} · {p.cliente_nome?.split(' ')[0]} · {p.item_nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 02 · Valor da NF */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-1.5 mb-4">02 · Valor da NF</p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <label className="text-xs font-black uppercase text-blue-700 mb-1 flex items-center gap-1">
                  <DollarSign size={10}/> Valor Total Faturado (R$) *
                  {pdfExtraido.valor_total && <span className="text-emerald-500 normal-case font-black ml-1">✓ PDF</span>}
                </label>
                <div className="relative">
                  <Input
                    value={formData.valor_total}
                    onChange={e => { setFormData({...formData, valor_total:e.target.value}); limparErro('valor_total'); }}
                    placeholder="0,00"
                    required
                    className={erros.valor_total
                      ? 'bg-red-50 border-2 border-red-400 rounded-lg h-10 text-sm font-black text-blue-900 px-3 w-full outline-none focus:border-red-500'
                      : pdfExtraido.valor_total
                        ? 'bg-emerald-50 border-2 border-emerald-400 rounded-lg h-10 text-sm font-black text-blue-900 px-3 w-full outline-none focus:border-emerald-500'
                        : 'bg-white border-blue-200 rounded-lg h-10 text-sm font-black text-blue-900 px-3 w-full outline-none focus:border-blue-500'}
                  />
                  {pdfExtraido.valor_total && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                </div>
                {erros.valor_total
                  ? <p className="text-xs text-red-600 font-black mt-1">{erros.valor_total}</p>
                  : <p className="text-xs text-blue-400 font-semibold mt-1">* Insira o valor real da NF (pode ter variação de até 10%)</p>}
              </div>
            </div>

          </div>{/* fim coluna esquerda */}

          {/* COLUNA DIREITA */}
          <div className="space-y-4">

            {/* 03 · Controle de Entrega */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-1.5 mb-4">03 · Controle de Entrega</p>

              {pedidoSelecionado?.itens?.length > 1 ? (
                /* MULTI-ITEM: tabela editável por item */
                <div className="rounded-xl overflow-hidden border border-slate-200">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-[#0A3D73] text-white font-black uppercase tracking-wide">
                        <th className="px-3 py-2 text-left">FE</th>
                        <th className="px-3 py-2 text-left">Descrição</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Pedido</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Entregue</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensEntrega.map((it, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 ${idx%2===0?'bg-white':'bg-slate-50/60'}`}>
                          <td className="px-3 py-2 font-black text-blue-700 font-mono whitespace-nowrap">{it.numero_fe||'—'}</td>
                          <td className="px-3 py-2 font-semibold text-slate-600 max-w-[100px] truncate">{it.item_nome||'—'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-500 font-mono whitespace-nowrap">{(it.quantidade||0).toLocaleString('pt-BR')}</td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              value={it.qtde_entregue}
                              onChange={e => handleQtdeEntregaItem(idx, e.target.value)}
                              className="w-20 border border-slate-200 rounded-lg bg-white px-2 h-7 text-xs font-semibold text-right text-slate-800 outline-none focus:border-[#0A3D73] transition-all"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-blue-700 font-mono whitespace-nowrap">
                            {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(it.valor_item||0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 border-t-2 border-slate-200 font-black text-slate-700 text-xs">
                        <td colSpan={2} className="px-3 py-2 uppercase tracking-wide">Total</td>
                        <td className="px-3 py-2 text-right font-mono">{qtdePedida.toLocaleString('pt-BR')}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {itensEntrega.reduce((s,it)=>s+(it.qtde_entregue||0),0).toLocaleString('pt-BR')}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-blue-700">
                          {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(
                            itensEntrega.reduce((s,it)=>s+(it.valor_item||0),0)
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {erros.qtde_entregue && (
                    <p className="text-xs text-red-600 font-black uppercase px-3 py-2 bg-red-50 border-t border-red-200">{erros.qtde_entregue}</p>
                  )}
                  {previewVariacao && (
                    <div className="p-3 space-y-2">
                      <div className={`px-3 py-2 border rounded-lg flex items-center gap-2 ${previewVariacao.cor}`}>
                        <Package size={12}/>
                        <span className="text-xs font-black uppercase">
                          Variação total: {previewVariacao.semaforo} {previewVariacao.label} {previewVariacao.ok ? '— dentro da tolerância ✓' : '— fora da tolerância!'}
                        </span>
                      </div>
                      {!previewVariacao.ok && (
                        <div className="space-y-2">
                          <label className={lbl}>Como tratar o saldo? *</label>
                          {[
                            { value: 'cliente_aceitou', label: '✅ Cliente aceitou — fechar' },
                            { value: 'fabrica_liquidou', label: '🏭 Fábrica liquidou — fechar' },
                            { value: 'entrega_parcial', label: `📦 Entrega parcial — criar saldo de ${Math.max(0, qtdePedida-(parseInt(formData.qtde_entregue)||0)).toLocaleString('pt-BR')} un` },
                          ].map(op => (
                            <label key={op.value} className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer text-xs font-black transition-all ${formData.motivo_entrega===op.value?'bg-blue-50 border-blue-400':'bg-white border-slate-200'}`}>
                              <input type="radio" name="motivo_nf" value={op.value} checked={formData.motivo_entrega===op.value} onChange={e=>setFormData({...formData,motivo_entrega:e.target.value})}/>
                              {op.label}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* SINGLE-ITEM: layout original sem alteração */
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>Qtde Pedida</label>
                      <div className="h-10 bg-white border border-slate-200 rounded-lg px-3 flex items-center text-xs font-black text-slate-500 font-mono">
                        {qtdePedida ? qtdePedida.toLocaleString('pt-BR') : '—'}
                      </div>
                    </div>
                    <div>
                      <label className={lbl}>Qtde Entregue {pdfExtraido.qtde_entregue && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={formData.qtde_entregue}
                          onChange={e => { setFormData({...formData, qtde_entregue:e.target.value}); limparErro('qtde_entregue'); }}
                          placeholder="0"
                          className={erros.qtde_entregue
                            ? 'w-full border-2 border-red-400 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-400/10 transition-all'
                            : inpCls('qtde_entregue')}
                        />
                        {pdfExtraido.qtde_entregue && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                      </div>
                      {erros.qtde_entregue && <p className="text-xs text-red-600 font-black mt-1">{erros.qtde_entregue}</p>}
                    </div>
                  </div>
                  {previewVariacao && (
                    <div className={`mt-2 px-3 py-2 border rounded-lg flex items-center gap-2 ${previewVariacao.cor}`}>
                      <Package size={12}/>
                      <span className="text-xs font-black uppercase">
                        Variação: {previewVariacao.semaforo} {previewVariacao.label} {previewVariacao.ok ? '— dentro da tolerância ✓' : '— fora da tolerância!'}
                      </span>
                    </div>
                  )}
                  {previewVariacao && !previewVariacao.ok && (
                    <div className="mt-3 space-y-2">
                      <label className={lbl}>Como tratar o saldo? *</label>
                      {[
                        { value: 'cliente_aceitou', label: '✅ Cliente aceitou — fechar' },
                        { value: 'fabrica_liquidou', label: '🏭 Fábrica liquidou — fechar' },
                        { value: 'entrega_parcial', label: `📦 Entrega parcial — criar saldo de ${Math.max(0, qtdePedida - (parseInt(formData.qtde_entregue) || 0)).toLocaleString('pt-BR')} un` },
                      ].map(op => (
                        <label key={op.value} className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer text-xs font-black transition-all ${formData.motivo_entrega === op.value ? 'bg-blue-50 border-blue-400' : 'bg-white border-slate-200'}`}>
                          <input type="radio" name="motivo_nf" value={op.value} checked={formData.motivo_entrega === op.value} onChange={e => setFormData({...formData, motivo_entrega:e.target.value})}/>
                          {op.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 04 · Cronograma de Vencimentos */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-1.5 mb-4">04 · Cronograma de Vencimentos</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div>
                  <label className={lbl}>1ª Parcela * {pdfExtraido.data_p1 && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                  <div className="relative">
                    <Input type="date" value={formData.data_p1} onChange={e => setFormData({...formData, data_p1:e.target.value})} required className={inpCls('data_p1')}/>
                    {pdfExtraido.data_p1 && <span className="absolute right-7 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                  </div>
                </div>
                {formData.numero_parcelas >= '2' && (
                  <div>
                    <label className={lbl}>2ª Parcela * {pdfExtraido.data_p2 && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                    <div className="relative">
                      <Input type="date" value={formData.data_p2} onChange={e => setFormData({...formData, data_p2:e.target.value})} required className={inpCls('data_p2')}/>
                      {pdfExtraido.data_p2 && <span className="absolute right-7 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                    </div>
                  </div>
                )}
                {formData.numero_parcelas >= '3' && (
                  <div>
                    <label className={lbl}>3ª Parcela * {pdfExtraido.data_p3 && <span className="text-emerald-500 normal-case font-black">✓ PDF</span>}</label>
                    <div className="relative">
                      <Input type="date" value={formData.data_p3} onChange={e => setFormData({...formData, data_p3:e.target.value})} required className={inpCls('data_p3')}/>
                      {pdfExtraido.data_p3 && <span className="absolute right-7 top-1/2 -translate-y-1/2 text-emerald-500 font-black text-xs pointer-events-none">✓</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>{/* fim coluna direita */}
        </div>

        {/* RODAPÉ — spanning ambas as colunas */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4 flex justify-end gap-3">
          <Link to="/notas-fiscais" className="px-6 py-2.5 text-xs font-black uppercase border border-slate-200 bg-white text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 text-xs font-black uppercase bg-[#0A3D73] hover:bg-[#082D54] text-white rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Salvando...' : 'Confirmar & Gerar Comissões'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NovaNF;
